/**
 * Parameter Validation Utilities
 * Provides validation and clamping functions for instrument parameters
 */
import type { InstrumentParameters, ADSRSettings, OscillatorSettings, FilterSettings, EffectsSettings, ParameterDefinition, PitchAutomationSettings, VibratoSettings, DrumTuningSettings, } from '../types/instruments';
/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}
/**
 * Validate and clamp a number value
 * Returns the clamped value and a warning message if clamping occurred
 */
export function validateNumber(value: number, min: number, max: number, paramName: string): {
    value: number;
    warning?: string;
} {
    if (typeof value !== 'number' || isNaN(value)) {
        console.warn(`Invalid ${paramName}: ${value}. Using default: ${min}`);
        return { value: min, warning: `Invalid ${paramName}, using minimum value` };
    }
    const clamped = clamp(value, min, max);
    if (clamped !== value) {
        console.warn(`${paramName} value ${value} out of range [${min}, ${max}]. Clamped to ${clamped}`);
        return { value: clamped, warning: `${paramName} adjusted to valid range` };
    }
    return { value: clamped };
}
/**
 * Validate ADSR settings
 */
export function validateADSR(adsr: Partial<ADSRSettings> | undefined): ADSRSettings {
    const defaults: ADSRSettings = {
        attack: 0.01,
        decay: 0.1,
        sustain: 0.7,
        release: 0.1,
    };
    if (!adsr) {
        return defaults;
    }
    try {
        return {
            attack: validateNumber(adsr.attack ?? defaults.attack, 0.001, 2.0, 'ADSR attack').value,
            decay: validateNumber(adsr.decay ?? defaults.decay, 0.001, 2.0, 'ADSR decay').value,
            sustain: validateNumber(adsr.sustain ?? defaults.sustain, 0.0, 1.0, 'ADSR sustain').value,
            release: validateNumber(adsr.release ?? defaults.release, 0.001, 2.0, 'ADSR release').value,
        };
    }
    catch (error) {
        console.error('Error validating ADSR settings:', error);
        return defaults;
    }
}
/**
 * Validate oscillator settings
 */
export function validateOscillator(oscillator: Partial<OscillatorSettings> | undefined): OscillatorSettings {
    if (!oscillator) {
        return {};
    }
    try {
        const validated: OscillatorSettings = {};
        if (oscillator.type) {
            const validTypes = ['sine', 'square', 'sawtooth', 'triangle', 'pulse'];
            if (validTypes.includes(oscillator.type)) {
                validated.type = oscillator.type;
            }
            else {
            }
        }
        if (oscillator.pulseWidth !== undefined) {
            validated.pulseWidth = validateNumber(oscillator.pulseWidth, 0.0, 1.0, 'Pulse width').value;
        }
        if (oscillator.harmonicity !== undefined) {
            validated.harmonicity = validateNumber(oscillator.harmonicity, 0.5, 8.0, 'Harmonicity').value;
        }
        if (oscillator.modulationIndex !== undefined) {
            validated.modulationIndex = validateNumber(oscillator.modulationIndex, 0, 20, 'Modulation index').value;
        }
        if (oscillator.detune !== undefined) {
            validated.detune = validateNumber(oscillator.detune, -1200, 1200, 'Detune').value;
        }
        return validated;
    }
    catch (error) {
        console.error('Error validating oscillator settings:', error);
        return {};
    }
}
/**
 * Validate filter settings
 */
export function validateFilter(filter: Partial<FilterSettings> | undefined): FilterSettings {
    if (!filter) {
        return {};
    }
    try {
        const validated: FilterSettings = {};
        if (filter.type) {
            const validTypes = ['lowpass', 'highpass', 'bandpass'];
            if (validTypes.includes(filter.type)) {
                validated.type = filter.type;
            }
            else {
            }
        }
        if (filter.frequency !== undefined) {
            validated.frequency = validateNumber(filter.frequency, 20, 20000, 'Filter frequency').value;
        }
        if (filter.resonance !== undefined) {
            validated.resonance = validateNumber(filter.resonance, 0.1, 10, 'Filter resonance').value;
        }
        if (filter.envelopeAmount !== undefined) {
            validated.envelopeAmount = validateNumber(filter.envelopeAmount, 0, 1, 'Filter envelope amount').value;
        }
        return validated;
    }
    catch (error) {
        console.error('Error validating filter settings:', error);
        return {};
    }
}
/**
 * Validate effects settings
 */
export function validateEffects(effects: Partial<EffectsSettings> | undefined): EffectsSettings {
    if (!effects) {
        return {};
    }
    try {
        const validated: EffectsSettings = {};
        if (effects.bitCrush !== undefined) {
            validated.bitCrush = Math.round(validateNumber(effects.bitCrush, 1, 16, 'Bit crush').value);
        }
        if (effects.distortion !== undefined) {
            validated.distortion = validateNumber(effects.distortion, 0, 1, 'Distortion').value;
        }
        if (effects.chorus !== undefined) {
            validated.chorus = Boolean(effects.chorus);
        }
        if (effects.delay) {
            validated.delay = effects.delay; // Delay settings are complex, validate separately if needed
        }
        return validated;
    }
    catch (error) {
        console.error('Error validating effects settings:', error);
        return {};
    }
}
/**
 * Validate pitch-automation settings (vibrato / pitch sweep).
 * Vibrato is normalized so rate/depth/delay are always present and in range —
 * the engine reads `delay` directly (triggerTime + delay), so a missing value
 * would produce NaN. Returns undefined when there is nothing to automate.
 */
export function validatePitchAutomation(pitchAutomation: PitchAutomationSettings | undefined): PitchAutomationSettings | undefined {
    if (!pitchAutomation) {
        return undefined;
    }
    try {
        const result: PitchAutomationSettings = {};
        if (pitchAutomation.vibrato) {
            const v = pitchAutomation.vibrato as Partial<VibratoSettings>;
            result.vibrato = {
                rate: validateNumber(v.rate ?? 6, 0.1, 20, 'Vibrato rate').value,
                depth: validateNumber(v.depth ?? 20, 0, 100, 'Vibrato depth').value,
                delay: validateNumber(v.delay ?? 0.1, 0, 2, 'Vibrato delay').value,
            };
        }
        if (pitchAutomation.pitchSweep) {
            // Pitch sweep is a compound shape — preserve as-is.
            result.pitchSweep = pitchAutomation.pitchSweep;
        }
        return result.vibrato || result.pitchSweep ? result : undefined;
    }
    catch (error) {
        console.error('Error validating pitch automation settings:', error);
        return undefined;
    }
}
/**
 * Validate drum-tuning settings (percussion buffer generation).
 * Only present fields are kept and clamped to the ranges the generators expect —
 * the engine reads these to regenerate the kick/snare/tom/etc. buffers.
 * Returns undefined when there is nothing to tune.
 */
export function validateDrumTuning(drumTuning: DrumTuningSettings | undefined): DrumTuningSettings | undefined {
    if (!drumTuning) {
        return undefined;
    }
    try {
        const result: DrumTuningSettings = {};
        if (drumTuning.pitchStart !== undefined) {
            result.pitchStart = validateNumber(drumTuning.pitchStart, 40, 500, 'Drum pitch start').value;
        }
        if (drumTuning.pitchEnd !== undefined) {
            result.pitchEnd = validateNumber(drumTuning.pitchEnd, 20, 300, 'Drum pitch end').value;
        }
        if (drumTuning.decayRate !== undefined) {
            result.decayRate = validateNumber(drumTuning.decayRate, 0.01, 1.0, 'Drum decay rate').value;
        }
        if (drumTuning.noiseTone !== undefined) {
            result.noiseTone = validateNumber(drumTuning.noiseTone, 200, 12000, 'Drum noise tone').value;
        }
        return Object.keys(result).length > 0 ? result : undefined;
    }
    catch (error) {
        console.error('Error validating drum tuning settings:', error);
        return undefined;
    }
}
/**
 * Validate complete instrument parameters
 */
export function validateInstrumentParameters(parameters: Partial<InstrumentParameters> | undefined): InstrumentParameters {
    try {
        const pitchAutomation = validatePitchAutomation(parameters?.pitchAutomation);
        const drumTuning = validateDrumTuning(parameters?.drumTuning);
        return {
            adsr: validateADSR(parameters?.adsr),
            oscillator: validateOscillator(parameters?.oscillator),
            filter: validateFilter(parameters?.filter),
            effects: validateEffects(parameters?.effects),
            // Preserve engine-honored fields the old validator silently dropped.
            ...(pitchAutomation ? { pitchAutomation } : {}),
            ...(drumTuning ? { drumTuning } : {}),
            ...(parameters?.dacQuantization !== undefined ? { dacQuantization: Boolean(parameters.dacQuantization) } : {}),
        };
    }
    catch (error) {
        console.error('Error validating instrument parameters:', error);
        // Return safe defaults
        return {
            adsr: { attack: 0.01, decay: 0.1, sustain: 0.7, release: 0.1 },
            oscillator: {},
            filter: {},
            effects: {},
        };
    }
}
/**
 * Validate parameter against its definition
 */
export function validateParameterAgainstDefinition(value: number, definition: ParameterDefinition): {
    value: number;
    warning?: string;
} {
    return validateNumber(value, definition.min, definition.max, definition.name);
}
/**
 * Check if parameters are valid (basic structure check)
 */
export function areParametersValid(parameters: any): parameters is InstrumentParameters {
    if (!parameters || typeof parameters !== 'object') {
        return false;
    }
    // Check ADSR if present
    if (parameters.adsr) {
        const adsr = parameters.adsr;
        if (typeof adsr.attack !== 'number' ||
            typeof adsr.decay !== 'number' ||
            typeof adsr.sustain !== 'number' ||
            typeof adsr.release !== 'number') {
            return false;
        }
    }
    return true;
}
